import React from 'react'
import classNames from 'classnames'

import { InfoTooltip } from '@/components/tooltips/InfoTooltip'

import './Card.scss'

/**
 * @import import { CardGrid } from '@/components/cards/Card'
 * @param {object} props
 * @param {node} props.children Cards laid out in a grid of fixed 300px columns
 * @param {boolean} [props.fill] Lets columns grow past 300px to fill the row width
 */
export const CardGrid = ({ children, fill, className, ...props }) => {
  return (
    <div className={classNames('card-grid', { fill }, className)} {...props}>
      {children}
    </div>
  )
}

/**
 * @import import { Card } from '@/components/cards/Card'
 * @param {object} props
 * @param {node} [props.title] Heading text; the card header only renders when set
 * @param {node} [props.subtitle] Secondary line rendered under the title
 * @param {node} [props.icon] Icon in the header, above the title row unless inlineIcon
 * @param {boolean} [props.inlineIcon] Renders the icon inline with the title text
 * @param {node} [props.info] InfoTooltip content shown next to the title
 * @param {node} [props.children] Card body content
 * @param {node} [props.buttons] Actions aligned to the right of the title
 * @param {node} [props.pills] Pills rendered at the bottom of the header
 * @param {boolean} [props.fullHeight] Stretches the card to fill its container height
 * @param {boolean} [props.compact] Reduces the card padding
 * @param {boolean} [props.clickable] Hover border for cards that act as links
 * @param {boolean} [props.flat] Removes the background and border
 */
export const Card = ({
  title,
  subtitle,
  icon,
  inlineIcon,
  info,
  children,
  buttons,
  pills,
  fullHeight,
  className,
  compact,
  clickable,
  flat,
  ...props
}) => {
  return (
    <div
      className={classNames('module-card', className, {
        'full-height': fullHeight,
        compact,
        clickable,
        flat,
      })}
      {...props}
    >
      {title && (
        <header className={classNames({ 'inline-icon': inlineIcon })}>
          {!inlineIcon && icon}
          <div className="module-card-header">
            <div className="module-card-title-group">
              {inlineIcon && icon}
              <h2>{title}</h2>
              {info && <InfoTooltip content={info} />}
            </div>
            {buttons}
          </div>
          {subtitle && <p className="module-card-subheader">{subtitle}</p>}
          {pills}
        </header>
      )}
      {children}
    </div>
  )
}
